import React, { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Accordion } from "react-bootstrap";
import { updatePluginToolbar } from "../../../common/actions";
import { getShape } from "../../../plugins/VectorShape/catalog/shapes";
import { resolveState } from "../../../plugins/VectorShape/defaults";
import { FillTab, RadiusTab, ShadowTab, StrokeColorTab, StrokeStyleTab, TextTab, t } from "./tabs";
import "./_shapeSettings.scss";

export const SHAPE_SECTIONS = [
  { key: "fill", i18nKey: "tab_fill", fallback: "Dolgu", cap: "fill", Body: FillTab },
  {
    key: "strokeColor",
    i18nKey: "tab_stroke_color",
    fallback: "Çizgi Rengi",
    cap: "stroke",
    Body: StrokeColorTab,
  },
  {
    key: "strokeStyle",
    i18nKey: "tab_stroke_style",
    fallback: "Çizgi Stili",
    cap: "stroke",
    Body: StrokeStyleTab,
  },
  {
    key: "radius",
    i18nKey: "tab_radius",
    fallback: "Köşe Yuvarlaklığı",
    cap: "radius",
    Body: RadiusTab,
  },
  { key: "shadow", i18nKey: "tab_shadow", fallback: "Gölge", cap: null, Body: ShadowTab },
  { key: "text", i18nKey: "tab_text", fallback: "Metin Kutusu", cap: "text", Body: TextTab },
];

// Idle time after which a run of edits (a slider drag, typing) ends its undo step.
const UNDO_IDLE_MS = 700;

// VectorShape settings as right-rail accordion sections, rendered by
// PluginToolbar above Yapı. Sections the shape can't use are left out.
// Edits are written straight to the plugin state; a continuous run of edits
// on one setting (a slider drag, typing, the native colour picker) is one
// undo step via window.undoGroupId.
const ShapeSettingsSections = ({ boxId, state }) => {
  const dispatch = useDispatch();
  const resolved = resolveState(state);
  const shape = getShape(resolved.shapeId) || getShape("rect");
  const idleTimer = useRef(null);
  const resolvedRef = useRef(resolved);
  resolvedRef.current = resolved;

  const endGroup = useCallback(() => {
    clearTimeout(idleTimer.current);
    idleTimer.current = null;
    if (String(window.undoGroupId || "").startsWith("shape-")) delete window.undoGroupId;
  }, []);

  useEffect(() => endGroup, [boxId, endGroup]);

  const update = useCallback(
    (key, patch) => {
      const current = resolvedRef.current;
      const value =
        patch !== null && typeof patch === "object" ? { ...current[key], ...patch } : patch;
      resolvedRef.current = { ...current, [key]: value };
      const id = `shape-${boxId}-${key}`;
      if (window.undoGroupId !== id) window.undoGroupId = id;
      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(endGroup, UNDO_IDLE_MS);
      dispatch(updatePluginToolbar(boxId, "main", "state", [key], [value]));
    },
    [boxId, dispatch, endGroup]
  );

  const sections = SHAPE_SECTIONS.filter((s) => !s.cap || shape.caps[s.cap]);

  return (
    <>
      {sections.map(({ key, i18nKey, fallback, Body }) => (
        <Accordion.Item
          key={`shape-${key}`}
          eventKey={`shape-${key}`}
          className="panelPluginToolbar"
          data-testid={`shape-section-${key}`}
        >
          <Accordion.Header>
            <div className="panel-heading">
              <p className="titleA">{t(i18nKey, fallback)}</p>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <div className="shape-settings">
              <Body state={resolved} shape={shape} update={update} />
            </div>
          </Accordion.Body>
        </Accordion.Item>
      ))}
    </>
  );
};

ShapeSettingsSections.propTypes = {
  boxId: PropTypes.string.isRequired,
  state: PropTypes.object,
};

export default ShapeSettingsSections;
